/**
 * Kanonischer Plattform-App-Katalog (Web + Mobile).
 * Single source of truth für Governance-Key, Routen und Mobile-Delivery.
 *
 * Web-Loader bleiben in apps/web — hier nur Metadaten und Mobile-Routing.
 */

/** @typedef {'native' | 'web'} PlatformMobileDelivery */

/**
 * @typedef {Readonly<{
 *   governanceKey: string,
 *   webPath: string,
 *   hubPath: string,
 *   titleKey: string,
 *   subtitleKey: string,
 *   mobileDelivery: PlatformMobileDelivery,
 *   nativeScreen?: string,
 *   showOnMobileHub: boolean,
 *   hubOrder: number,
 * }>} PlatformAppCatalogEntry
 */

/** @type {ReadonlyArray<PlatformAppCatalogEntry>} */
export const PLATFORM_APP_CATALOG = Object.freeze([
  {
    governanceKey: "trainerstunden",
    webPath: "/trainingsdesk",
    hubPath: "/hub/trainingsdesk",
    titleKey: "hub.trainerstunden.title",
    subtitleKey: "hub.premium.trainerstunden.taglineBook",
    mobileDelivery: "native",
    nativeScreen: "TrainingsDesk",
    showOnMobileHub: true,
    hubOrder: 10,
  },
  {
    governanceKey: "trainer",
    webPath: "/trainer",
    hubPath: "/hub/trainer",
    titleKey: "hub.trainer.title",
    subtitleKey: "hub.trainer.subtitle",
    mobileDelivery: "native",
    nativeScreen: "TrainerStudio",
    showOnMobileHub: true,
    hubOrder: 20,
  },
  {
    governanceKey: "matchmixer",
    webPath: "/matchmixer",
    hubPath: "/hub/matchmixer",
    titleKey: "hub.schleifchen.title",
    subtitleKey: "hub.premium.matchmixer.tagline",
    mobileDelivery: "native",
    nativeScreen: "MatchMixerHome",
    showOnMobileHub: true,
    hubOrder: 30,
  },
  {
    governanceKey: "clubhub",
    webPath: "/clubhub",
    hubPath: "/hub/clubhub",
    titleKey: "hub.clubhub.title",
    subtitleKey: "hub.premium.clubhub.tagline",
    mobileDelivery: "native",
    nativeScreen: "ClubHub",
    showOnMobileHub: true,
    hubOrder: 40,
  },
  {
    governanceKey: "clubmeisterschaft",
    webPath: "/clubmeisterschaft",
    hubPath: "/hub/clubmeisterschaft",
    titleKey: "hub.clubmeisterschaft.title",
    subtitleKey: "hub.premium.clubmeisterschaft.tagline",
    mobileDelivery: "native",
    nativeScreen: "ClubmeisterschaftHome",
    showOnMobileHub: true,
    hubOrder: 50,
  },
  {
    governanceKey: "medenspiele",
    webPath: "/medenspiele",
    hubPath: "/hub/medenspiele",
    titleKey: "hub.medenspiele.title",
    subtitleKey: "hub.premium.medenspiele.tagline",
    mobileDelivery: "native",
    nativeScreen: "Medenspiele",
    showOnMobileHub: true,
    hubOrder: 60,
  },
  {
    governanceKey: "belegung",
    webPath: "/belegung",
    hubPath: "/hub/belegung",
    titleKey: "hub.belegung.title",
    subtitleKey: "hub.premium.belegung.tagline",
    mobileDelivery: "web",
    nativeScreen: null,
    showOnMobileHub: true,
    hubOrder: 65,
  },
  {
    governanceKey: "kalender",
    webPath: "/kalender",
    hubPath: "/hub/kalender",
    titleKey: "hub.kalender.title",
    subtitleKey: "hub.premium.kalender.tagline",
    mobileDelivery: "web",
    nativeScreen: null,
    showOnMobileHub: true,
    hubOrder: 66,
  },
  {
    governanceKey: "insights",
    webPath: "/insights",
    hubPath: "/hub/insights",
    titleKey: "hub.insights.title",
    subtitleKey: "hub.apps.tagline.insights",
    mobileDelivery: "web",
    nativeScreen: null,
    showOnMobileHub: true,
    hubOrder: 68,
  },
  {
    governanceKey: "matchflow",
    webPath: "/matchflow",
    hubPath: "/hub/matchflow",
    titleKey: "hub.matchflow.title",
    subtitleKey: "hub.matchflow.subtitle",
    mobileDelivery: "web",
    nativeScreen: null,
    showOnMobileHub: false,
    hubOrder: 5,
  },
  {
    governanceKey: "winterdoppel",
    webPath: "/serientermine",
    hubPath: "/hub/serientermine",
    titleKey: "hub.winterdoppel.title",
    subtitleKey: "hub.premium.winterdoppel.tagline",
    mobileDelivery: "web",
    nativeScreen: null,
    showOnMobileHub: true,
    hubOrder: 67,
  },
  {
    governanceKey: "tennis-live",
    webPath: "/tenniswelt",
    hubPath: "/hub/tenniswelt",
    titleKey: "hub.tenniswelt.title",
    subtitleKey: "hub.tenniswelt.subtitle",
    mobileDelivery: "native",
    nativeScreen: "TennisLive",
    showOnMobileHub: true,
    hubOrder: 70,
  },
  {
    governanceKey: "minigames",
    webPath: "/minigames",
    hubPath: "/hub/minigames",
    titleKey: "hub.minigames.title",
    subtitleKey: "mobile.home.webModuleSubtitle",
    mobileDelivery: "web",
    showOnMobileHub: true,
    hubOrder: 80,
  },
]);

/** Governance-Key „schleifchen“ → MatchMixer (Legacy-Alias). */
export const PLATFORM_GOVERNANCE_KEY_ALIASES = Object.freeze({
  schleifchen: "matchmixer",
});

/**
 * @param {string} governanceKey
 * @returns {PlatformAppCatalogEntry | null}
 */
export function getPlatformAppCatalogEntry(governanceKey) {
  const raw = String(governanceKey || "")
    .trim()
    .toLowerCase();
  const key = PLATFORM_GOVERNANCE_KEY_ALIASES[raw] || raw;
  return PLATFORM_APP_CATALOG.find((e) => e.governanceKey === key) || null;
}

/**
 * Mobile-Hub-Einträge in Anzeige-Reihenfolge.
 * @returns {ReadonlyArray<PlatformAppCatalogEntry>}
 */
export function getMobileHubCatalogEntries() {
  return PLATFORM_APP_CATALOG.filter((e) => e.showOnMobileHub).sort(
    (a, b) => a.hubOrder - b.hubOrder,
  );
}

/**
 * @param {string} webPath
 * @returns {PlatformAppCatalogEntry | null}
 */
export function resolvePlatformAppByWebPath(webPath) {
  const normalized = `/${String(webPath || "")
    .trim()
    .replace(/^\/+/, "")
    .replace(/\/+$/, "")}`;
  if (!normalized || normalized === "/") return null;

  // Legacy URL-Aliase Serientermine
  const aliased = (() => {
    if (
      normalized === "/winterdoppel" ||
      normalized.startsWith("/winterdoppel/")
    ) {
      return normalized.replace(/^\/winterdoppel/, "/serientermine");
    }
    if (
      normalized === "/hub/winterdoppel" ||
      normalized.startsWith("/hub/winterdoppel/")
    ) {
      return normalized.replace(/^\/hub\/winterdoppel/, "/hub/serientermine");
    }
    if (
      normalized === "/regeltermine" ||
      normalized.startsWith("/regeltermine/")
    ) {
      return normalized.replace(/^\/regeltermine/, "/serientermine");
    }
    if (
      normalized === "/hub/regeltermine" ||
      normalized.startsWith("/hub/regeltermine/")
    ) {
      return normalized.replace(/^\/hub\/regeltermine/, "/hub/serientermine");
    }
    return normalized;
  })();

  let best = null;
  let bestLen = -1;
  for (const entry of PLATFORM_APP_CATALOG) {
    const base = entry.webPath.replace(/\/+$/, "");
    if (aliased === base || aliased.startsWith(`${base}/`)) {
      if (base.length > bestLen) {
        best = entry;
        bestLen = base.length;
      }
    }
  }
  return best;
}
